import { useState, type FormEvent } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Plane, ArrowLeft, Loader2 } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';

type LoginStep = 'email' | 'otp';

export function LoginPage() {
  const { requestOtp, verifyOtp, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [step, setStep] = useState<LoginStep>('email');
  const [email, setEmail] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/';

  // Redirect if already authenticated
  if (isAuthenticated) {
    navigate(from, { replace: true });
    return null;
  }

  const handleRequestOtp = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      await requestOtp(email);
      setStep('otp');
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to send verification code';
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyOtp = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      await verifyOtp(email, otpCode);
      navigate(from, { replace: true });
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Invalid verification code';
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    setStep('email');
    setOtpCode('');
    setError('');
  };

  return (
    <div className="flex min-h-screen">
      {/* Left panel - branding */}
      <div className="hidden w-1/2 flex-col justify-between bg-aviation-950 p-12 lg:flex">
        <div className="flex items-center gap-3">
          <Plane className="h-8 w-8 text-aviation-400" />
          <span className="text-2xl font-bold text-white">AVGRP</span>
        </div>
        <div>
          <h1 className="text-4xl font-bold leading-tight text-white">
            Aviation Ground
            <br />
            Resource Planning
          </h1>
          <p className="mt-4 max-w-md text-lg text-gray-400">
            Manage flights, allocate resources, and optimize ground operations
            from a single dashboard.
          </p>
        </div>
        <p className="text-sm text-gray-600">
          &copy; {new Date().getFullYear()} AVGRP. All rights reserved.
        </p>
      </div>

      {/* Right panel - login form */}
      <div className="flex w-full items-center justify-center bg-white px-6 lg:w-1/2">
        <div className="w-full max-w-sm">
          {/* Mobile logo */}
          <div className="mb-8 flex items-center gap-2 lg:hidden">
            <Plane className="h-6 w-6 text-aviation-600" />
            <span className="text-xl font-bold text-gray-900">AVGRP</span>
          </div>

          {step === 'email' ? (
            <>
              <h2 className="text-2xl font-bold text-gray-900">Sign in</h2>
              <p className="mt-2 text-sm text-gray-500">
                Enter your email address to receive a verification code.
              </p>

              <form onSubmit={handleRequestOtp} className="mt-8 space-y-5">
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Email address
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    required
                    autoFocus
                    autoComplete="email"
                    className="input-field mt-1.5"
                  />
                </div>

                {error && (
                  <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting || !email}
                  className="btn-primary w-full"
                >
                  {isSubmitting ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : null}
                  {isSubmitting ? 'Sending code...' : 'Continue'}
                </button>
              </form>
            </>
          ) : (
            <>
              <button
                onClick={handleBack}
                className="mb-6 flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </button>

              <h2 className="text-2xl font-bold text-gray-900">
                Enter verification code
              </h2>
              <p className="mt-2 text-sm text-gray-500">
                We sent a 6-digit code to{' '}
                <span className="font-medium text-gray-700">{email}</span>
              </p>

              <form onSubmit={handleVerifyOtp} className="mt-8 space-y-5">
                <div>
                  <label
                    htmlFor="otp"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Verification code
                  </label>
                  <input
                    id="otp"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]{6}"
                    maxLength={6}
                    value={otpCode}
                    onChange={(e) =>
                      setOtpCode(e.target.value.replace(/\D/g, ''))
                    }
                    placeholder="000000"
                    required
                    autoFocus
                    autoComplete="one-time-code"
                    className="input-field mt-1.5 text-center text-2xl font-mono tracking-[0.5em]"
                  />
                </div>

                {error && (
                  <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting || otpCode.length !== 6}
                  className="btn-primary w-full"
                >
                  {isSubmitting ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : null}
                  {isSubmitting ? 'Verifying...' : 'Verify & Sign in'}
                </button>

                <button
                  type="button"
                  onClick={handleRequestOtp}
                  disabled={isSubmitting}
                  className="w-full text-center text-sm text-aviation-600 hover:text-aviation-700"
                >
                  Resend code
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
